import { Avatar } from "../components/Avatar";
import { Button } from "../components/Button";
import { Card, CardActionButton } from "../components/Card";
import { Chip } from "../components/Chip";
import { TruncatedText } from "../components/TruncatedText";
import { Minus, Plus, XClose } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import React from "react";
const FADE_TRANSITION_CLASSES = "transition-opacity ease-in-out";
export const ACTION_CARD_DIFF_STATUSES = ["added", "removed"];
const actionCardDiffVariants = cva("p-3", {
    variants: {
        diffStatus: {
            added: cn("bg-success-50 border-success-200"),
            removed: cn("bg-warning-50 border-warning-200"),
        },
    },
});
const DIFF_CHIP_CONFIG = {
    added: { color: "success", icon: Plus },
    removed: { color: "warning", icon: Minus },
};
/**
 * A card representing a capability or tool in the agent builder, showing an icon,
 * label, and description with an optional footer link. It has three modes: selectable
 * (`canAdd` + `isSelected`) for toggling a tool on an agent, display-only
 * (`canAdd={false}`), and diff (`diffStatus` of `added` / `removed`) for pending changes.
 * Use it to list an agent's tools and let builders add or remove them, or to preview
 * toolset changes. For an in-conversation accept/reject action proposal, use
 * ActionCardBlock instead.
 * @summary Tool card for the agent builder.
 */
export const ActionCard = React.forwardRef(({ icon, iconBackgroundColor, iconColor, label, description, isSelected, canAdd, cantAddReason, disabled, diffStatus, footer, onRemove, onClick, className, cardContainerClassName, mountPortal, mountPortalContainer, descriptionLineClamp = 2, }, ref) => {
    const diffChip = diffStatus ? DIFF_CHIP_CONFIG[diffStatus] : null;
    return (React.createElement(Card, { ref: ref, variant: isSelected ? "secondary" : "primary", onClick: onClick, disabled: disabled !== null && disabled !== void 0 ? disabled : !canAdd, containerClassName: cardContainerClassName, className: cn(actionCardDiffVariants({ diffStatus }), className), action: onRemove ? (React.createElement(CardActionButton, { size: "icon", icon: XClose, onClick: (e) => {
                onRemove();
                e.stopPropagation();
            } })) : undefined },
        React.createElement("div", { className: "flex h-full w-full flex-col justify-between" },
            React.createElement("div", { className: "flex flex-col" },
                React.createElement("div", { className: "mb-2 flex items-center justify-between gap-2" },
                    React.createElement("div", { className: "flex min-w-0 items-center gap-2" },
                        React.createElement(Avatar, { icon: icon, size: "xs", backgroundColor: iconBackgroundColor, iconColor: iconColor }),
                        React.createElement("span", { className: "truncate text-sm font-medium" }, label),
                        isSelected && (React.createElement(Chip, { size: "mini", color: "success", label: "ADDED", className: cn(FADE_TRANSITION_CLASSES, "opacity-100") }))),
                    React.createElement("div", { className: "flex flex-shrink-0 items-center gap-2" },
                        diffChip && (React.createElement(Chip, { size: "xs", color: diffChip.color, icon: diffChip.icon, className: cn(FADE_TRANSITION_CLASSES, "opacity-100") })),
                        canAdd && (React.createElement(Button, { size: "xs", variant: "outline", icon: Plus, label: "Add", className: cn(FADE_TRANSITION_CLASSES, "flex-shrink-0") })),
                        cantAddReason && (React.createElement("div", { className: "flex-shrink-0 text-xs italic text-muted-foreground" }, cantAddReason)))),
                React.createElement(TruncatedText, { className: "text-xs text-muted-foreground", mountPortal: mountPortal, mountPortalContainer: mountPortalContainer, lineClamp: descriptionLineClamp }, description)),
            footer && (React.createElement("div", null, footer.onClick ? (React.createElement("a", { onClick: (e) => {
                    var _a;
                    e.stopPropagation();
                    (_a = footer.onClick) === null || _a === void 0 ? void 0 : _a.call(footer);
                }, className: "text-xs cursor-pointer text-muted-foreground hover:text-highlight-light hover:underline hover:underline-offset-2" }, footer.label)) : (React.createElement("span", { className: "text-xs text-muted-foreground" }, footer.label)))))));
});
ActionCard.displayName = "ActionCard";
//# sourceMappingURL=ActionCard.js.map